import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type RowData,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { useSearchParams } from 'react-router';

// Extend TanStack Table meta
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
  }
}

import { type Device, loraApi } from '@/api/connectivity/lora';
import { JobStatusTable } from '@/components/connectivity/job-status-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { Separator } from '@/components/ui/separator';
import { TableSkeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/context/auth-context';
import { toast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';

// Table columns
const columns: ColumnDef<Device>[] = [
  {
    header: 'Serial',
    accessorKey: 'equipment.serial',
    cell: ({ row }) => row.original.equipment.serial,
  },
  {
    header: 'Modelo',
    accessorKey: 'model',
    cell: ({ row }) => {
      const model = row.original.model;
      if (model === 'UNRECOGNIZED') {
        return (
          <Badge
            className="gap-2 bg-orange-100 border-orange-300 text-orange-900"
            variant="outline"
          >
            <AlertTriangle size={14} />
            Desconhecido
          </Badge>
        );
      }
      return model;
    },
  },
  {
    header: 'EUI',
    accessorKey: 'equipment.lora_keys.device_eui',
    cell: ({ row }) => row.original.equipment?.lora_keys?.device_eui?.toLowerCase() ?? '--',
  },
  {
    header: 'Timestamp',
    accessorKey: 'equipment.lora_keys.timestamp',
    cell: ({ row }) => row.original.equipment?.lora_keys?.timestamp ?? '--',
  },
  {
    header: 'AppKey',
    accessorKey: 'equipment.lora_keys.application_key',
    cell: ({ row }) => row.original.equipment?.lora_keys?.application_key ?? '--',
  },
  {
    header: 'Status',
    accessorKey: 'equipment.lora_keys.kora_active',
    cell: ({ row }) => {
      const status = row.original.equipment.lora_keys?.kora_active ?? false;
      return <Badge variant="outline">{status ? 'Ativo' : 'Inativo'}</Badge>;
    },
  },
  {
    header: 'Criado em',
    accessorKey: 'created_at',
    cell: ({ row }) => {
      try {
        return format(new Date(row.original.created_at), 'dd/MM/yyyy HH:mm');
      } catch {
        return '--';
      }
    },
  },
];

export function KoraSyncPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const limit = 20;

  // Query for KORA devices
  const { data, isLoading } = useQuery({
    queryKey: ['kora-devices', page],
    queryFn: () => loraApi.findKoraDevices({ page, limit }),
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () =>
      loraApi.syncKora({
        author: {
          user_uuid: user?.id ?? '',
          name: user?.name ?? '',
        },
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Requisição enviada com sucesso!',
        description: 'Sincronização em andamento. O processo poderá levar alguns minutos!',
      });
      queryClient.invalidateQueries({ queryKey: ['kora-devices'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao sincronizar',
        description: getApiErrorMessage(error, error.message),
        variant: 'destructive',
      });
    },
  });

  // Table instance
  const table = useReactTable({
    data: data?.docs ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleSync = (e: React.FormEvent) => {
    e.preventDefault();
    syncMutation.mutate();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col min-h-full space-y-6 p-6 py-10"
    >
      {/* Header */}
      <header className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Sincronização KORA</h1>
          <p className="text-sm text-muted-foreground">
            Busque e sincronize os dispositivos KORA com o BCube.
          </p>
        </div>

        <form onSubmit={handleSync} className="flex items-center gap-2">
          <Button type="submit" disabled={syncMutation.isPending}>
            <RefreshCcw className={cn(syncMutation.isPending && 'animate-spin')} />
            Efetuar sincronização
          </Button>
        </form>
      </header>

      <Separator />

      {/* Table Section */}
      <section>
        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : (
          <div className="space-y-4">
            <div className="border border-border rounded-md w-full overflow-y-auto">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(header.column.columnDef.meta?.className)}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>

                <TableBody>
                  {table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow className="hover:bg-background" key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-24 text-center">
                        Nenhum dado encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {(data?.pages ?? 1) > 1 && (
              <div className="overflow-hidden rounded-md border">
                <Pagination
                  currentPage={page}
                  totalPages={data?.pages ?? 1}
                  totalItems={data?.total ?? 0}
                  limit={limit}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        )}
      </section>

      <Separator />

      {/* Jobs Section — acompanha o processamento assíncrono dos jobs KORA */}
      <section>
        <JobStatusTable />
      </section>
    </motion.div>
  );
}
